import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    MenuItem,
    TextField,
    Alert,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    Chip,
    Avatar,
    useTheme,
    alpha
} from '@mui/material';
import {
    Inventory as InventoryIcon,
    History as HistoryIcon,
    Category as CategoryIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import api from '../../utils/api';
import BranchInventory from '../branch/BranchInventory';
import InventoryTransactionHistory from '../branch/InventoryTransactionHistory';
import PageContainer from '../shared/PageContainer';

function TabPanel(props) {
    const { children, value, index, lazy = false, hasLoaded, ...other } = props;
    
    // Only render content if not lazy, or if lazy and has been loaded before
    const shouldRender = !lazy || (value === index) || hasLoaded;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`inventory-tabpanel-${index}`}
            aria-labelledby={`inventory-tab-${index}`}
            {...other}
        >
            {value === index && shouldRender && (
                <Box sx={{ pt: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const InventoryManagement = () => {
    const theme = useTheme();
    const { user } = useAuth();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [branches, setBranches] = useState([]);
    const [selectedBranchId, setSelectedBranchId] = useState('');
    const [inventoryStats, setInventoryStats] = useState({
        totalProducts: 0,
        lowStockItems: 0
    });
    // Cache shared data to prevent duplicate API calls
    const [sharedData, setSharedData] = useState({
        products: null,
        categories: null,
        loadedAt: null
    });
    // Track which tabs have been loaded for lazy loading
    const [loadedTabs, setLoadedTabs] = useState({ 0: true, 1: false, 2: false });

    // Check if navigated from product creation
    const navigationState = location.state;
    const newProductId = navigationState?.newProductId;
    const showAddInventory = navigationState?.showAddInventory;

    useEffect(() => {
        initializeData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedBranchId) {
            fetchInventoryStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBranchId]);

    // Initialize all required data in parallel
    const initializeData = async () => {
        try {
            setLoading(true);
            
            // Fetch branches and shared data in parallel
            const [branchesResponse, productsResponse, categoriesResponse] = await Promise.all([
                user.role === 'admin' 
                    ? api.get('/branches')
                    : api.get(`/user-branches/${user.id}`),
                api.get('/products'),
                api.get('/products/categories')
            ]);
            
            // Set branches
            const branchesData = branchesResponse.data.branches || [];
            setBranches(branchesData);
            
            if (branchesData.length > 0) {
                setSelectedBranchId(branchesData[0].id.toString());
            }
            
            // Cache shared data
            setSharedData({
                products: productsResponse.data.products || [],
                categories: categoriesResponse.data.categories || [],
                loadedAt: Date.now()
            });
            
            setError('');
        } catch (error) {
            console.error('Error initializing data:', error);
            setError('Failed to load inventory data. Please refresh the page.');
        } finally {
            setLoading(false);
        }
    };

    // Refresh shared data when needed (e.g., after adding new products)
    const refreshSharedData = async () => {
        try {
            const [productsResponse, categoriesResponse] = await Promise.all([
                api.get('/products'),
                api.get('/products/categories')
            ]);
            
            setSharedData({
                products: productsResponse.data.products || [],
                categories: categoriesResponse.data.categories || [],
                loadedAt: Date.now()
            });
        } catch (error) {
            console.error('Error refreshing shared data:', error);
        }
    };

    const fetchInventoryStats = async () => {
        try {
            const inventoryRes = await api.get(`/branches/${selectedBranchId}/inventory`);
            const inventory = inventoryRes.data.inventory || [];

            const lowStockItems = inventory.filter(item => {
                const quantity = parseFloat(item.quantity) || 0;
                const reorderLevel = parseFloat(item.reorder_level) || 0;
                return quantity < reorderLevel && reorderLevel > 0;
            }).length;

            setInventoryStats({
                totalProducts: inventory.length,
                lowStockItems
            });
        } catch (error) {
            console.error('Error fetching inventory stats:', error);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // Mark tab as loaded when user clicks on it
        setLoadedTabs(prev => ({ ...prev, [newValue]: true }));
    };

    const handleBranchChange = (event) => {
        setSelectedBranchId(event.target.value);
        setActiveTab(0); // Reset to first tab when changing branches
    };

    const selectedBranch = branches.find(b => b.id.toString() === selectedBranchId);

    if (loading) {
        return (
            <PageContainer title="Inventory Management">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer title="Inventory Management">
                <Alert severity="error">{error}</Alert>
            </PageContainer>
        );
    }

    if (branches.length === 0) {
        return (
            <PageContainer title="Inventory Management">
                <Alert severity="info">
                    You haven't been assigned to any branches yet. Please contact your administrator to get branch access for inventory management.
                </Alert>
            </PageContainer>
        );
    }

    return (
        <PageContainer
            title="Inventory Management"
            subtitle="Manage stock levels across your branches"
        >
            {/* Branch Selection */}
            <Card
                elevation={0}
                sx={{
                    p: 3,
                    mb: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                }}
            >
                <Grid container spacing={3} alignItems="center">
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            select
                            label="Select Branch"
                            value={selectedBranchId}
                            onChange={handleBranchChange}
                            disabled={branches.length <= 1}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    bgcolor: alpha(theme.palette.background.default, 0.6),
                                }
                            }}
                        >
                            {branches.map((branch) => (
                                <MenuItem key={branch.id} value={branch.id.toString()}>
                                    {branch.name}
                                    {branch.is_main_branch && (
                                        <Chip
                                            label="Main"
                                            size="small"
                                            sx={{
                                                ml: 1,
                                                height: 22,
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                color: 'primary.main',
                                            }}
                                        />
                                    )}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 8 }}>
                        {selectedBranch && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: 'primary.main',
                                        fontWeight: 700,
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    {selectedBranch.name?.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                            {selectedBranch.name}
                                        </Typography>
                                        {selectedBranch.is_main_branch && (
                                            <Chip
                                                label="Main Branch"
                                                size="small"
                                                sx={{
                                                    height: 22,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600,
                                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                                    color: 'success.dark',
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedBranch.address || 'No address provided'}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Card>

            {/* Inventory Stats */}
            {selectedBranchId && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card
                            elevation={0}
                            sx={{
                                height: '100%',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.info.main, 0.15)}`,
                                }
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Total Products
                                    </Typography>
                                    <Avatar
                                        sx={{
                                            width: 42,
                                            height: 42,
                                            bgcolor: alpha(theme.palette.info.main, 0.08),
                                            color: theme.palette.info.main,
                                        }}
                                    >
                                        <CategoryIcon />
                                    </Avatar>
                                </Box>
                                <Typography variant="h4" component="div" sx={{ fontWeight: 700, fontSize: '1.75rem', color: 'text.primary' }}>
                                    {inventoryStats.totalProducts}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Items in branch inventory
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Card
                            elevation={0}
                            sx={{
                                height: '100%',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 24px ${alpha(
                                        inventoryStats.lowStockItems > 0 ? theme.palette.warning.main : theme.palette.success.main,
                                        0.15
                                    )}`,
                                }
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Low Stock Items
                                    </Typography>
                                    <Avatar
                                        sx={{
                                            width: 42,
                                            height: 42,
                                            bgcolor: inventoryStats.lowStockItems > 0
                                                ? alpha(theme.palette.warning.main, 0.08)
                                                : alpha(theme.palette.success.main, 0.08),
                                            color: inventoryStats.lowStockItems > 0
                                                ? theme.palette.warning.main
                                                : theme.palette.success.main,
                                        }}
                                    >
                                        <WarningIcon />
                                    </Avatar>
                                </Box>
                                <Typography variant="h4" component="div" sx={{ fontWeight: 700, fontSize: '1.75rem', color: inventoryStats.lowStockItems > 0 ? 'warning.main' : 'text.primary' }}>
                                    {inventoryStats.lowStockItems}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {inventoryStats.lowStockItems > 0 ? 'Items below reorder level' : 'All stock levels healthy'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Main Content Tabs */}
            {selectedBranchId && (
                <Card elevation={0} sx={{ width: '100%', borderRadius: 3 }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            aria-label="inventory management tabs"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    minHeight: 56,
                                    '&.Mui-selected': {
                                        fontWeight: 600,
                                    },
                                },
                            }}
                        >
                            <Tab
                                icon={<InventoryIcon sx={{ fontSize: 20 }} />}
                                label="Current Inventory"
                                iconPosition="start"
                            />
                            <Tab
                                icon={<HistoryIcon sx={{ fontSize: 20 }} />}
                                label="Transaction History"
                                iconPosition="start"
                            />
                        </Tabs>
                    </Box>

                    <TabPanel value={activeTab} index={0} hasLoaded={loadedTabs[0]}>
                        <BranchInventory 
                            branchId={selectedBranchId}
                            onInventoryUpdate={fetchInventoryStats}
                            newProductId={newProductId}
                            showAddInventory={showAddInventory}
                            cachedProducts={sharedData.products}
                            cachedCategories={sharedData.categories}
                            onRefreshSharedData={refreshSharedData}
                        />
                    </TabPanel>

                    <TabPanel value={activeTab} index={1} lazy={true} hasLoaded={loadedTabs[1]}>
                        <InventoryTransactionHistory
                            branchId={selectedBranchId}
                        />
                    </TabPanel>

                </Card>
            )}
        </PageContainer>
    );
};

export default InventoryManagement; 